import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  IonContent,
  IonPage,
  isPlatform,
  RefresherEventDetail,
  useIonRouter,
  useIonViewWillEnter,
  useIonViewDidEnter,
  useIonViewDidLeave,
  useIonViewWillLeave,
} from '@ionic/react';
import { useHistory } from 'react-router';
import { useIntl } from 'react-intl';
import { ImpactStyle } from '@capacitor/haptics';
import { useTranslation } from 'react-i18next';
import {
  Container,
  decOfNum,
  isSpecialistBlocked,
  Loading,
  PushNotificationsModal,
  Refresher,
  Session,
  SignUpSessionModal,
  Specialist,
  useEvent,
} from '@mindly/ui-components';

import { lastActiveSubscriptionSelector, subscriptionsSelector } from '../../redux/subscriptions/selector';
import { loadConsultationsThunk } from '../../redux/consultations/action';
import { fetchSubscriptions } from '../../redux/subscriptions/thunk';
import { userPassedOrSkipOnBoardingSelector, userSelector } from '../../redux/user/selector';
import { getUsersPsychologistsListThunk } from '../../redux/usersPsychologists/action';
import { usersPsychologistsSelector } from '../../redux/usersPsychologists/selector';
import { ISubscription } from '../../redux/subscriptions/types';
import { PrivateRoutes, PublicRoutes } from '../../routes/navigation';
import { consultationsSelector } from '../../redux/consultations/selector';
import { setActiveTab, setIsActive, Tabs } from '../../redux/tabBar/reducer';
import PageHeader from '../../components/PageHeader/PageHeader';
import SubscriptionStatus from '../../components/SubscriptionStatus';
import { FIRST_BOOKING_FAQ } from '../../intercom-articles';
import { useAppointment } from '../../hooks/useAppointment/useAppointment';
import useToast from '../../context/ToastContext/ToastContext';
import { useAnalytics } from '../../shared/hooks/useAnalytics';
import { usePushNotifications } from '../../shared/context/PushNotificationsContext';
import useLangs from '../../context/LangsContext';
import { fetchPromoCodes } from '../../redux/promoCodes/promoCodes.thunk';
import { useSignAgreementsContext } from '../../context/SignAgreementsContext/SignAgreements.context';
import { TranslatedSessionsListWidget } from '../../translated-components';
import SessionStart, { SessionStartRef } from '../../components/SessionStart/SessionStart';
import {
  setSubscriptionSessionReviewModalIsOpen,
  setTrialSessionReviewModalIsOpen,
} from '../../redux/consultationReviews/consultationsReviews.reducer';
import { setSessionForReview } from '../../redux/consultationReviews/consultationsReviews.reducer';
import { getFutureConsultationsByUserId, getPreviousConsultationsByUserId } from '../../api/consultation';
import {
  futureSessionsSelector,
  previousSessionsSelector,
  hasMorePreviousSessionsSelector,
  futureSessionsLoadedSelector,
  previousSessionsLoadedSelector,
} from '../../redux/consultations/newList.selector';
import {
  setFutureSessions,
  setPreviousSessions,
  setPreviousSessionsTotal,
} from '../../redux/consultations/newList.reducer';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';
import { ANIMATION_DURATION } from 'src/shared/utils/pageAnimation';

const pushNotificationPermissionImage = {
  uk: '/assets/push-notifications/uk.webp',
  pl: '/assets/push-notifications/pl.webp',
  es: '/assets/push-notifications/sp.webp',
};
const MAX_PERIOD_TO_REVIEW_PAST_SESSION_IN_HOURS = 1000 * 24;

const SchedulePage = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const history = useHistory();
  const intl = useIntl();
  const { trackPredefinedEvent } = useAnalytics();
  const { locale } = useLangs();
  const { pushToAppointmentPage } = useAppointment();
  const state = usePushNotifications();
  const { t, i18n } = useTranslation(['app', 'schedule', 'ui']);
  const user = useSelector(userSelector);
  const usersPsychologistList = useSelector(usersPsychologistsSelector);
  const userPassedOrSkipOnBoarding = useSelector(userPassedOrSkipOnBoardingSelector);
  const subscriptions = useSelector(subscriptionsSelector());
  const consultations = useSelector(consultationsSelector);
  const lastSubscription = useSelector(lastActiveSubscriptionSelector());
  const [, setIsLoading] = useState(!consultations?.length);
  const [isLoadingPreviousSessions, setIsLoadingPreviousSessions] = useState(false);
  const [isLoadingFutureSessions, setIsLoadingFutureSessions] = useState(false);
  const [isSignupModalOpen, setIsSignUpModalOpen] = useState<boolean>(false);
  const { schedulePresentingElement } = useSignAgreementsContext();
  const sessionStartRef = useRef<SessionStartRef>(null);
  const navigation = useIonRouter();
  const [isAppointmentLoading, setIsAppointmentLoading] = useState<boolean>(false);

  const futureSessions = useSelector(futureSessionsSelector);
  const previousSessions = useSelector(previousSessionsSelector);
  const hasMorePreviousSessions = useSelector(hasMorePreviousSessionsSelector);
  const futureSessionsLoaded = useSelector(futureSessionsLoadedSelector);
  const previousSessionsLoaded = useSelector(previousSessionsLoadedSelector);
  const [isMounted, setIsMounted] = useState(true);
  const [showScheduleSessionButton, setShowScheduleSessionButton] = useState(false);

  useIonViewDidEnter(() => {
    trackPredefinedEvent('SCHEDULE_SCREEN_VIEWED');
  }, [trackPredefinedEvent]);

  useIonViewWillEnter(() => {
    if (!user?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }
    dispatch(setIsActive(true));
    dispatch(setActiveTab(Tabs.second));
    setTimeout(() => {
      // show schedule session button after animation tabs bar
      setShowScheduleSessionButton(true);
    }, ANIMATION_DURATION + 50);
  }, [user?.id]);

  useIonViewDidEnter(() => {
    setIsMounted(true);
  });

  useIonViewWillLeave(() => {
    if (navigation.routeInfo.pathname?.includes(PrivateRoutes.SESSION_DETAIL.replace('/:id', ''))) {
      setShowScheduleSessionButton(false);
    }
  }, [navigation.routeInfo]);

  useIonViewDidLeave(() => {
    setIsMounted(false);
  });

  useEffect(() => {
    if (!isPlatform('hybrid')) return;

    state.setIsOpenPushNotificationModal(state.pushNotificationStatus === 'prompt');
  }, [state.setIsOpenPushNotificationModal, state.pushNotificationStatus]);

  useEffect(() => {
    if (!user?.id || !isMounted) return;

    initFutureSessionsList(true);
    initPreviousSessionsList(true);
  }, [user?.id, isMounted]);

  useEffect(() => {
    if (!user?.id || !userPassedOrSkipOnBoarding) {
      return setIsLoading(false);
    }

    (async () => {
      try {
        if (!consultations?.length) {
          setIsLoading(true);
        }
        await Promise.all([
          consultations?.length ? undefined : dispatch(loadConsultationsThunk(user?.id)),
          dispatch(fetchSubscriptions(user?.id)),
          usersPsychologistList?.length ? undefined : dispatch(getUsersPsychologistsListThunk()),
        ]);
      } catch (error) {
        showToast({ error });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [user?.id, consultations?.length, usersPsychologistList?.length]);

  const initFutureSessionsList = async (considerCache = false) => {
    if (considerCache && futureSessions.length > 0) {
      return;
    }
    if (!futureSessionsLoaded) {
      setIsLoadingFutureSessions(true);
    }
    const futureSessionsList = await getFutureConsultationsByUserId();
    dispatch(setFutureSessions(futureSessionsList));
    setIsLoadingFutureSessions(false);
  };

  const initPreviousSessionsList = async (considerCache = false) => {
    if (considerCache && previousSessions.length > 0) {
      return;
    }
    if (!previousSessionsLoaded) {
      setIsLoadingPreviousSessions(true);
    }
    const previousSessionsList = await getPreviousConsultationsByUserId();
    dispatch(setPreviousSessions(previousSessionsList.list));
    dispatch(setPreviousSessionsTotal(previousSessionsList?.total ?? 0));
    setIsLoadingPreviousSessions(false);
  };

  const sessionsCount = useCallback(
    (s?: ISubscription) => {
      if (!s) return 0;

      return s.session_count - s.used_balance;
    },
    [subscriptions]
  );

  const userSpecialistsList = useMemo(
    () =>
      !usersPsychologistList
        ? []
        : usersPsychologistList?.map((s) => {
            const sessions = sessionsCount(subscriptions.data?.find((sub) => sub.therapist_id === s.id));

            return {
              id: s?.id,
              name: s?.first_name && `${s?.first_name} ${s?.last_name[0]}.`,
              avatar: s?.avatar_link ?? '',
              isBlockedText: isSpecialistBlocked(s as unknown as Specialist)
                ? t('ui:specialist_status.is_blocked')
                : undefined,
              sessions: `${sessions} ${decOfNum(sessions, [
                intl.formatMessage({
                  id: 'other.sessions.one',
                  defaultMessage: 'сеанс',
                }),
                intl.formatMessage({
                  id: 'other.sessions.many',
                  defaultMessage: 'сеанси',
                }),
                intl.formatMessage({
                  id: 'other.sessions.several',
                  defaultMessage: 'сеансів',
                }),
              ])}`,
            };
          }),
    [usersPsychologistList?.length, sessionsCount, decOfNum, intl]
  );

  const refreshConsultations = async (e: CustomEvent<RefresherEventDetail>) => {
    if (user?.id) {
      try {
        setIsLoading(true);
        await Promise.all([
          await dispatch(loadConsultationsThunk(user.id)),
          await dispatch(fetchSubscriptions(user.id)),
          await dispatch(getUsersPsychologistsListThunk()),
          dispatch(
            fetchPromoCodes({
              client_id: user?.id,
            })
          ).unwrap(),
          state.updatePermissions(),
        ]);
        await initFutureSessionsList();
        await initPreviousSessionsList();
      } catch (error) {
        showToast({ error });
      } finally {
        setIsLoading(false);
        e.detail.complete();
      }
    }
  };
  const handleSignUpSpecialist = useCallback(
    async (id: string) => {
      try {
        setIsAppointmentLoading(true);
        await pushToAppointmentPage(id);
      } finally {
        setIsSignUpModalOpen(false);
        setIsAppointmentLoading(false);
      }
    },
    [subscriptions, history, pushToAppointmentPage]
  );

  const handleSignUpSessionBtn = useCallback(async () => {
    if (usersPsychologistList && usersPsychologistList?.length === 1) {
      try {
        setIsAppointmentLoading(true);
        await pushToAppointmentPage(usersPsychologistList[0]?.id);
      } finally {
        setIsAppointmentLoading(false);
      }
    } else {
      setIsSignUpModalOpen(true);
    }
  }, [usersPsychologistList?.length, handleSignUpSpecialist, pushToAppointmentPage]);

  const handleSessionStart = useEvent(async (session: Session) => {
    trackPredefinedEvent('SESSION_START_BUTTON_CLICKED', {
      specialist_id: session?.psychologist_id ?? `${`undefined-session-id-${session?.id}`}`,
      specialist_name: `${session?.therapist?.first_name ?? 'undefined'} ${session?.therapist?.last_name ?? 'undefined'}`,
      is_trial: !!session.payment_intent_id,
    });
    sessionStartRef.current?.openSession(session);
  });

  const handleSessionClick = useCallback((session: Session) => {
    isomorficHapticsImpact({ style: ImpactStyle.Light });
    navigation.push(PrivateRoutes.SESSION_DETAIL.replace(':id', session.id));
  }, []);

  const handleRateSession = useCallback((session: Session) => {
    dispatch(setSessionForReview(session));
    if (!session.session_review) {
      if (session.payment_intent_id) {
        dispatch(setTrialSessionReviewModalIsOpen(true));
      } else {
        dispatch(setSubscriptionSessionReviewModalIsOpen(true));
      }
      trackPredefinedEvent('SCHEDULE_SCREEN_RATE_SESSION_CLICKED', {
        specialist_id: session.psychologist_id,
        specialist_name: session.psychologist_name,
        consultation_id: session.id,
        subscription_id: session.subscription_id ?? null,
      });
    }
  }, []);

  const handleLoadPreviousSessions = async () => {
    try {
      const sessions = await getPreviousConsultationsByUserId(`offset=${previousSessions.length}`);
      dispatch(setPreviousSessions([...previousSessions, ...(sessions?.list ?? [])]));
      dispatch(setPreviousSessionsTotal(sessions?.total ?? 0));
      return {
        list: sessions?.list ?? [],
        total: sessions?.total ?? 0,
      };
    } catch (error) {
      showToast({ error });
      return {
        list: [],
        total: 0,
      };
    }
  };

  return (
    <IonPage ref={schedulePresentingElement}>
      <PageHeader
        intercomId={lastSubscription ? undefined : FIRST_BOOKING_FAQ}
        showChatButton
        showAvatar={false}
        childrenInCenterSlot
        showPromoCodeTimer
      >
        <div className="flex">
          <SubscriptionStatus />
        </div>
      </PageHeader>
      <IonContent>
        <Refresher onIonRefresh={refreshConsultations} disabled={false} />
        <Container style={{ paddingTop: 0, height: '100%' }}>
          <TranslatedSessionsListWidget
            futureSessions={futureSessions as Session[]}
            previousSessions={previousSessions as Session[]}
            hasMorePreviousSessions={hasMorePreviousSessions}
            locale={i18n.language}
            isLoadingFutureSessions={isLoadingFutureSessions}
            isLoadingPreviousSessions={isLoadingPreviousSessions}
            maxPeriodToReviewPastSessionInHours={MAX_PERIOD_TO_REVIEW_PAST_SESSION_IN_HOURS}
            loadPreviousSessions={handleLoadPreviousSessions}
            onSessionStart={handleSessionStart}
            onSessionClick={handleSessionClick}
            rateSessionCallback={handleRateSession}
            onScheduleSession={
              !!consultations?.length &&
              usersPsychologistList?.filter((el) => !!el)?.length &&
              showScheduleSessionButton
                ? handleSignUpSessionBtn
                : undefined
            }
          />
        </Container>
      </IonContent>
      <Loading isOpen={isAppointmentLoading} />
      <SessionStart ref={sessionStartRef} />
      <SignUpSessionModal
        isOpen={isSignupModalOpen}
        specialists={userSpecialistsList}
        onCancel={() => setIsSignUpModalOpen(false)}
        onSpecialistSelected={handleSignUpSpecialist}
        viewportHeight={window?.innerHeight ?? 500}
        title={intl.formatMessage({
          id: 'schedule.choose.title',
          defaultMessage: 'Вибери психолога',
        })}
        cancelBtnLabel={intl.formatMessage({
          id: 'general.cancel.btn',
          defaultMessage: 'Скасувати',
        })}
      />
      <PushNotificationsModal
        canDismiss={state.canDismiss}
        image={pushNotificationPermissionImage[locale as 'uk' | 'pl']}
        presentingElement={schedulePresentingElement.current ? schedulePresentingElement.current : undefined}
        isOpen={state.isOpenPushNotificationModal}
        onApplyPermission={state.allowPushNotificationHandler}
        title={t('app:allow.notifications.first.title')}
        description={t('app:allow.notifications.first.description')}
        applyPermissionText={t('app:allow.notifications.first.button')}
      />
    </IonPage>
  );
};

export default SchedulePage;
